/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { Suspense, useEffect, useImperativeHandle, useRef, useState, type Ref } from 'react';
import { Await, useFetcher } from 'react-router';
import type { ContentAssetBody } from '@/lib/api/content-asset';
import { useTranslation } from 'react-i18next';
import { Frown, RotateCcw, CircleAlert, CircleCheck, Download, Printer } from 'lucide-react';
import { Button } from '@/components/buttons';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/forms/input';
import { Label } from '@/components/forms/label';
import { RadioGroup, RadioGroupItem } from '@/components/forms/radio-group';
import { SynchronyIcon } from '@/components/icons';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
    DialogClose,
} from '@/components/dialog';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { resolveAssetUrl } from '@/lib/utils';
import { buildPdpPrequalUrl } from '@/lib/synchrony/prequal-links';
import { detectFinanceCardType } from '@/lib/payment/finance-card-type';
import type { AppConfig } from '@/types/config';
import { isTermSelectable, resolveMinimumPurchaseAmount } from './ashley-advantage-payment-utils';
import AshleyAdvantageAccountTooltip from './ashley-advantage-account-tooltip';

/** Ashley Advantage® wordmark shown in the account-lookup modal header (Figma node 37303:77569). */
const ashleyAdvantageLogo = resolveAssetUrl('/images/ashley-advantage-logo.png');
import type {
    FinancingTerm,
    SynchronyFinanceTermsResponse,
    SynchronyFinanceTermsErrorResponse,
} from '@/routes/action.synchrony-finance-terms';
import type { SynchronyFinanceDisclosureResponse } from '@/routes/action.synchrony-finance-disclosure';

/** Disclosure fetcher payload — the action returns either the success body or the shared error envelope. */
type DisclosureFetcherData = SynchronyFinanceDisclosureResponse | { success: false; errorCode: string; error: string };

export interface SelectedFinancingTerm {
    /**
     * ≤4-char Synchrony `ThirdPartyTermCode` (e.g. "197"). Used by the Synchrony v3
     * authorization endpoint (`sync.v3.auth`) and by the Synchrony promo-disclosure
     * endpoint (both cap the code at 4 characters). NOT accepted by APG's
     * `/authorizations` endpoint — use {@link apgCode} for that branch instead.
     */
    termCode: string;
    /**
     * APG canonical term code (e.g. `SYNC191`, `GEN6003`). Used by the shared APG
     * `ashley.payment/authorizations` endpoint on both the Genesis branch and the
     * Synchrony-with-v3-disabled fallback. Legacy `LibAshleyPayment._addFinanceCardToRequest`
     * puts this exact value on `body.FinancingOptionCode`. Sending the
     * `ThirdPartyTermCode` here returns HTTP 422 "Finance terms code not found".
     */
    apgCode: string;
    description: string;
    accountNumber: string;
    /** Security code (CVV) entered with the account number. Empty unless enableSynchronyCVV is on. */
    securityCode: string;
    /**
     * The Synchrony promo disclosure HTML text for the accepted term. Captured from the lazy
     * `/action/synchrony-finance-disclosure` call so the exact legal text the shopper agreed to
     * is stamped onto the order (`paymentInstrument.custom.financeTermsAndConditions`) instead of
     * being re-fetched server-side (where a repeat call sometimes returns null and leaves the
     * XML export blank). Empty string when Synchrony returned no disclosure for the term.
     */
    disclosureText: string;
}

/** Imperative handle so the panel's "View Financing Options" CTA can live outside this component (matches Figma footer button). */
export interface AshleyAdvantageHandle {
    /** Validate the entered account number and fetch eligible financing offers. */
    viewOffers: () => void;
}

interface AshleyAdvantagePaymentProps {
    basketTotal: number;
    /** Prefills the account-number field when returning from the accepted-summary "Edit" flow. */
    initialAccountNumber?: string;
    /** Called once the shopper selects a financing term and accepts disclosures */
    onTermSelected: (term: SelectedFinancingTerm) => void;
    /**
     * When true (mirrors the `enableSynchronyCVV` site preference), a security-code (CVV) field is
     * revealed after a full 16-digit account number is entered, and the value is sent on the
     * Synchrony account-lookup + authorization calls (legacy parity).
     */
    cvvEnabled?: boolean;
    /** Toggles strict terms-only mode in the parent payment section. */
    onTermsScreenChange?: (active: boolean) => void;
    /**
     * BM content asset body for the Terms Agreement legal copy (consent paragraphs + "Important
     * Promotional Information"). Streamed from the checkout loader so it never blocks TTFB. Empty
     * body renders when the asset is missing/unpublished — BM is the single source of truth, there
     * is no in-code fallback (same contract as the Delivery Details modals). The per-term
     * promotional disclosure shown beneath this body is a separate live Synchrony call.
     */
    termsAgreementContentAsset?: Promise<ContentAssetBody | null>;
    /** React 19 ref-as-prop exposing {@link AshleyAdvantageHandle}. */
    ref?: Ref<AshleyAdvantageHandle>;
}

type Phase = 'entry' | 'loading' | 'terms' | 'error';
type TermsStep = 'offerSelection' | 'termsAgreement';

const TERMS_MODAL_BASE_CLASS =
    'w-[968px] max-w-[calc(100vw-2rem)] sm:!max-w-[968px] rounded-xs border border-black/5 p-8 shadow-lg';
const TERMS_MODAL_CLASS_BY_STEP: Record<TermsStep, string> = {
    offerSelection: `${TERMS_MODAL_BASE_CLASS} gap-5`,
    termsAgreement: `${TERMS_MODAL_BASE_CLASS} gap-6`,
};

function resolveThirdPartyTermCode(term: FinancingTerm): string {
    const explicit = (term.thirdPartyTermCode || '').trim();
    if (explicit) return explicit;
    const fromCode = (term.code || '').replace(/\D/g, '').slice(-4);
    return fromCode;
}

function resolveGenesisDisclosureForTerm(term: FinancingTerm | undefined): string {
    if (!term) return '';
    const normalize = (value: string): string => value.replace(/\s+/g, ' ').trim();
    const typedFinanceDisclosure = (term.financeDisclosureHtml || '').trim();
    const typedTermsAndConditions = (term.termsAndConditionsHtml || '').trim();

    // Defensive legacy/APG payload parsing:
    // some sandboxes return disclosure fields with legacy casing
    // (FinanceDisclosures / LearnMore) or nested arrays.
    const raw = term as unknown as Record<string, unknown>;

    const asString = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');
    const directFinanceDisclosure = asString(raw.FinanceDisclosures) || asString(raw.financeDisclosures);
    const directTermsAndConditions =
        asString(raw.LearnMore) ||
        asString(raw.learnMore) ||
        asString(raw.termsandconditions) ||
        asString(raw.termsAndConditions);

    const disclosureArray = raw.financeDisclosures || raw.promotionDisclosures;
    let arrayDisclosure = '';
    if (Array.isArray(disclosureArray) && disclosureArray.length > 0) {
        const first = disclosureArray[0] as Record<string, unknown>;
        arrayDisclosure = asString(first?.description) || asString(first?.text);
    }

    // Concora legal body requires BOTH sections when available:
    // - consent/terms body (legacy LearnMore)
    // - promotional disclosure body (legacy FinanceDisclosures)
    const termsBody = typedTermsAndConditions || directTermsAndConditions;
    const disclosureBody = typedFinanceDisclosure || directFinanceDisclosure || arrayDisclosure;

    if (termsBody && disclosureBody) {
        const termsNorm = normalize(termsBody);
        const disclosureNorm = normalize(disclosureBody);
        // Avoid duplicate rendering when one field already contains the other.
        if (termsNorm.includes(disclosureNorm)) return termsBody;
        if (disclosureNorm.includes(termsNorm)) return disclosureBody;
        return `${termsBody}<br/><br/>${disclosureBody}`;
    }

    return termsBody || disclosureBody || '';
}

/**
 * Minimal client-side sanitizer for provider-managed HTML.
 */
function sanitizeProviderHtml(html: string | null | undefined): string {
    if (!html) return '';
    if (typeof window === 'undefined') return html;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll('script, style, iframe, object, embed, link, meta').forEach((node) => node.remove());

    doc.querySelectorAll('*').forEach((element) => {
        for (const attr of [...element.attributes]) {
            const name = attr.name.toLowerCase();
            const value = attr.value.trim().toLowerCase();
            if (name.startsWith('on')) {
                element.removeAttribute(attr.name);
                continue;
            }
            if (
                (name === 'href' || name === 'src' || name === 'xlink:href') &&
                (value.startsWith('javascript:') || value.startsWith('data:'))
            ) {
                element.removeAttribute(attr.name);
            }
        }
    });

    return doc.body.innerHTML;
}

/**
 * AshleyAdvantagePayment — checkout payment panel for the Ashley Advantage® card.
 *
 * Flow (matches AshCom Design System "Payment" panel):
 * 1. Shopper enters their Ashley Advantage account number + security code
 * 2. The footer "View Financing Options" button (owned by the parent Payment step) calls
 *    {@link AshleyAdvantageHandle.viewOffers}, which POSTs to /action/synchrony-finance-terms
 *    → Synchrony account lookup validates the account + returns eligible terms
 * 3. Shopper selects a term (e.g. 60-month same-as-cash) and accepts disclosures
 * 4. onTermSelected callback fires so the parent can set the basket payment instrument
 *
 * The "Account look-up" button opens an in-page modal (AshCom "AA Account Lookup Modal") where the
 * shopper can verify their Ashley Advantage account number against Synchrony before requesting
 * financing offers. On a successful look-up the account number is prefilled into the entry field and
 * the modal closes so the shopper can continue to "View Financing Options".
 */
export default function AshleyAdvantagePayment({
    basketTotal,
    initialAccountNumber = '',
    onTermSelected,
    cvvEnabled = false,
    onTermsScreenChange,
    termsAgreementContentAsset,
    ref,
}: AshleyAdvantagePaymentProps) {
    const { t } = useTranslation(['checkout', 'synchrony', 'common', 'errors']);
    const ta = t as (key: string, options?: Record<string, unknown>) => string;
    const { synchrony } = useConfig<AppConfig>();
    const prequalUrl = buildPdpPrequalUrl({
        uniqueId: synchrony?.uniqueId ?? '',
        // Checkout-placement pre-qualify link. Legacy `prequalifyAN.checkout`
        // uses the same siteCode as the checkout eapply popup (af0culyd1).
        siteCode: synchrony?.checkoutEapplySiteCode ?? '',
    });

    const [accountNumber, setAccountNumber] = useState(initialAccountNumber);
    const [accountError, setAccountError] = useState('');
    // Security code (CVV) — only shown/required when cvvEnabled and a full 16-digit account is entered.
    const [securityCode, setSecurityCode] = useState('');
    const [cvvError, setCvvError] = useState('');
    const [phase, setPhase] = useState<Phase>('entry');
    const [terms, setTerms] = useState<FinancingTerm[]>([]);
    const [isGenesisPayment, setIsGenesisPayment] = useState(false);
    const [providerDisclosureHtml, setProviderDisclosureHtml] = useState<string>('');
    const [selectedTermCode, setSelectedTermCode] = useState('');
    const [termsStep, setTermsStep] = useState<TermsStep>('offerSelection');
    const [serverError, setServerError] = useState('');
    // Mirror the deferred `termsAgreementContentAsset` Promise into state so the outer component
    // can gate Accept Terms on whether the shopper has any visible legal body. `undefined` = not
    // yet resolved, `null` = resolved but no asset (missing/unpublished/rejected in the loader),
    // otherwise the raw body markup. The `<Await>` below still handles the render; this hook is
    // purely a signal for the button + missing-body callout.
    const [resolvedTermsAgreementBody, setResolvedTermsAgreementBody] = useState<string | null | undefined>(undefined);

    // Per-term promo disclosure is fetched LAZILY (interaction-driven) when the shopper expands/
    // selects a term — it is no longer returned by /synchrony-finance-terms (that would add a third
    // sequential outbound call to the 10s-capped terms lookup). useFetcher is the correct primitive
    // here: the fetch is triggered by a user action, not on initial render. The disclosure is dynamic
    // (it varies by account + basket amount + promotion code), so it is fetched live per term.
    const disclosureFetcher = useFetcher<DisclosureFetcherData>();
    const disclosureHtml =
        disclosureFetcher.data && disclosureFetcher.data.success ? disclosureFetcher.data.disclosureHtml : null;
    const isDisclosureLoading = disclosureFetcher.state !== 'idle';
    // The Synchrony branch gates Accept Terms on "at least one legal body is present". Track whether
    // the per-term disclosure fetch has round-tripped at least once so we don't show the missing-body
    // error during the brief render window between entering the Terms step and useFetcher flipping
    // to `submitting`. `data === undefined` means the fetcher has never resolved.
    const isDisclosureSettled = disclosureFetcher.state === 'idle' && disclosureFetcher.data !== undefined;
    const selectedTerm = terms.find((tm) => tm.code === selectedTermCode);
    const genesisDisclosureHtml = (
        resolveGenesisDisclosureForTerm(selectedTerm) ||
        providerDisclosureHtml ||
        disclosureHtml ||
        ''
    ).trim();
    const sanitizedGenesisDisclosureHtml = sanitizeProviderHtml(genesisDisclosureHtml);
    const sanitizedDisclosureHtml = sanitizeProviderHtml(disclosureHtml ?? '');

    /**
     * Lazily fetch the promo disclosure for a single term. No-ops if the inputs needed by the
     * Custom API (16-digit account, positive total, short thirdPartyTermCode) are not all present.
     */
    const fetchDisclosure = (term: FinancingTerm) => {
        const cleanAccount = accountNumber.replace(/\D/g, '');
        const thirdPartyTermCode = resolveThirdPartyTermCode(term);
        if (cleanAccount.length < 16 || !(basketTotal > 0) || !thirdPartyTermCode) {
            return;
        }
        const fd = new FormData();
        fd.set('accountNumber', cleanAccount);
        fd.set('basketTotal', String(basketTotal));
        // Synchrony requires the ≤4-char ThirdPartyTermCode, not the APG `code` (e.g. "SYNC197").
        fd.set('termCode', thirdPartyTermCode);
        void disclosureFetcher.submit(fd, { method: 'POST', action: '/action/synchrony-finance-disclosure' });
    };

    // Account-lookup modal (AshCom "AA Account Lookup Modal") — an in-page form that verifies the
    // shopper's account number against Synchrony (via /action/synchrony-account-lookup → the
    // Custom SCAPI account-lookup endpoint → getAccountInfo). On success the verified account number
    // is prefilled into the entry field and the modal closes.
    const [lookupOpen, setLookupOpen] = useState(false);
    const [lookupAccountNumber, setLookupAccountNumber] = useState('');
    const [lookupCvv, setLookupCvv] = useState('');
    const [lookupAccountError, setLookupAccountError] = useState('');
    const [lookupCvvError, setLookupCvvError] = useState('');
    const [lookupError, setLookupError] = useState('');
    const [isLookupLoading, setIsLookupLoading] = useState(false);
    // After a successful verification the modal shows a confirmation step (rather than silently
    // closing) so the shopper gets clear positive feedback; "Continue" then prefills + closes.
    const [lookupVerified, setLookupVerified] = useState(false);
    const termsContentRef = useRef<HTMLDivElement | null>(null);
    // Prevent stale finance-terms responses from overwriting the latest card/account attempt.
    // This is important when shoppers switch providers quickly (e.g. Synchrony -> Concora).
    const financeTermsAbortRef = useRef<AbortController | null>(null);
    const financeTermsRequestIdRef = useRef(0);
    // Ref over the asset body so handleDownloadTerms / handlePrintTerms read the same DOM the
    // shopper saw. Avoids having to keep a separate plaintext copy in code (would drift the moment
    // a merchandiser edits the BM asset). Empty when the asset is missing — handlers fall back to
    // the heading-only download (legacy parity for "I clicked Download and got the header".)
    const termsAgreementBodyRef = useRef<HTMLDivElement | null>(null);
    const hasGenesisDisclosure = genesisDisclosureHtml.length > 0;
    // Synchrony-only compliance gate: the shopper must see either the BM Terms Agreement body OR
    // the per-term Synchrony promo disclosure before we let them click Accept Terms. When BOTH
    // resolve empty we surface the same missing-disclosure callout the Concora branch already
    // shows (aa-concora-disclosure-missing) and disable the Accept button. See loader — the
    // Promise always resolves to `null` on any BM failure, and the failure is logged there.
    const isTermsAgreementAssetResolved = resolvedTermsAgreementBody !== undefined;
    const hasTermsAgreementAssetBody = Boolean(resolvedTermsAgreementBody?.trim());
    const hasSynchronyLegalBody = hasTermsAgreementAssetBody || Boolean(disclosureHtml);
    const showSynchronyLegalBodyMissing =
        !isGenesisPayment &&
        termsStep === 'termsAgreement' &&
        isTermsAgreementAssetResolved &&
        isDisclosureSettled &&
        !hasSynchronyLegalBody;
    const formatUsd = (value: number) =>
        new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);

    // Reveal the CVV field in the modal once the feature is on AND a full 16-digit account number
    // is entered AND the account BIN routes to Synchrony. Legacy parity (SSF-407): Concora
    // (Genesis) cards do NOT collect a security code — the field must stay hidden for those BINs.
    const cleanLookupAccount = lookupAccountNumber.replace(/\D/g, '');
    const lookupCardType = detectFinanceCardType(cleanLookupAccount);
    const showLookupCvvField = cvvEnabled && cleanLookupAccount.length === 16 && lookupCardType === 'Synchrony';

    const handleLookupAccountNumberChange = (value: string) => {
        const cleaned = value.replace(/[^\d\s]/g, '');
        setLookupAccountNumber(cleaned);
        if (lookupAccountError) setLookupAccountError('');
        if (lookupError) setLookupError('');
        // Drop any CVV typed while on a Synchrony BIN if the shopper edits into a Concora BIN,
        // so we don't forward a stale security code to /synchrony-account-lookup.
        if (detectFinanceCardType(cleaned) !== 'Synchrony') {
            if (lookupCvv) setLookupCvv('');
            if (lookupCvvError) setLookupCvvError('');
        }
    };

    const handleLookupCvvChange = (value: string) => {
        setLookupCvv(value.replace(/\D/g, '').slice(0, 4));
        if (lookupCvvError) setLookupCvvError('');
    };

    const handleLookupSubmit = async () => {
        if (cleanLookupAccount.length < 16) {
            setLookupAccountError(ta('ashleyAdvantage.accountNumberInvalid'));
            return;
        }

        const cleanLookupCvv = lookupCvv.replace(/\D/g, '');
        // CVV is only collected for Synchrony BINs (legacy parity — SSF-407). Skip the required-
        // field validation entirely when the BIN routes to Concora so those shoppers can proceed.
        if (cvvEnabled && lookupCardType === 'Synchrony' && cleanLookupCvv.length < 3) {
            setLookupCvvError(ta('ashleyAdvantage.cvvInvalid'));
            return;
        }

        setIsLookupLoading(true);
        setLookupError('');
        setLookupAccountError('');

        try {
            const formData = new FormData();
            formData.set('accountNumber', cleanLookupAccount);
            if (cvvEnabled && lookupCardType === 'Synchrony' && cleanLookupCvv) {
                formData.set('cvv', cleanLookupCvv);
            }

            const res = await fetch('/action/synchrony-account-lookup', {
                method: 'POST',
                body: formData,
            });
            const data = (await res.json()) as
                | { success: true }
                | {
                      success: false;
                      errorCode?: string;
                      error?: string;
                      invalidAccount?: boolean;
                      // Legacy `{success:false, timeout:false}` unknown-BIN envelope. The storefront
                      // treats it the same as any other invalid account (keep the shopper on the
                      // field with the retry message) — the SCAPI endpoint intentionally omits an
                      // `error` string to avoid leaking provider names.
                      timeout?: boolean;
                  };

            if (!data.success) {
                // Differentiate a correctable account problem from a technical/service fault.
                // `invalidAccount` (Synchrony BIN, v3 lookup could not verify) → keep the shopper
                // on the field with red helper text so they can fix the number. The legacy
                // unknown-BIN envelope (`timeout:false`, no `error`) is treated the same way —
                // the shopper needs to correct the number. Anything else (SERVICE_UNAVAILABLE:
                // vendor unreachable, missing config, no token) is NOT the shopper's fault —
                // surface it in the prominent error callout instead of masquerading as a "wrong
                // account number" field error.
                if (data.invalidAccount || data.timeout === false) {
                    setLookupAccountError(data.error || ta('ashleyAdvantage.accountNumberInvalid'));
                } else {
                    setLookupError(data.error || t('errors:genericTryAgain'));
                }
                return;
            }

            // Verified → show the in-modal confirmation step. The shopper clicks "Continue" to
            // prefill the entry field and close (handleLookupConfirm), so the verification result
            // is acknowledged instead of the modal silently disappearing.
            setLookupVerified(true);
        } catch {
            // Network / unexpected client-side error — surface a generic message.
            // Server-side action handlers re-throw with structured logging.
            setLookupError(t('errors:genericTryAgain'));
        } finally {
            setIsLookupLoading(false);
        }
    };

    // Apply the verified account (and CVV when collected) to the payment panel entry field and close
    // the modal. Called from the confirmation step's "Continue" button.
    const handleLookupConfirm = () => {
        const cleanLookupCvv = lookupCvv.replace(/\D/g, '');
        setAccountNumber(cleanLookupAccount);
        setAccountError('');
        if (cvvEnabled && lookupCardType === 'Synchrony' && cleanLookupCvv) {
            setSecurityCode(cleanLookupCvv);
            setCvvError('');
        }
        setLookupVerified(false);
        setLookupOpen(false);
    };

    // Reveal the CVV field once the feature is on AND a full 16-digit account number is entered
    // AND the BIN routes to Synchrony. Legacy parity (SSF-407): Concora (Genesis) cards do NOT
    // collect a security code — the field stays hidden and unrequired for those BINs. The Genesis
    // authorization path (APG) doesn't accept a securityCode field.
    const cleanAccountNumber = accountNumber.replace(/\D/g, '');
    const detectedCardType = detectFinanceCardType(cleanAccountNumber);
    const showCvvField = cvvEnabled && cleanAccountNumber.length === 16 && detectedCardType === 'Synchrony';

    const handleAccountNumberChange = (value: string) => {
        // Allow digits + spaces for readability; strip on submit
        const cleaned = value.replace(/[^\d\s]/g, '');
        setAccountNumber(cleaned);
        if (accountError) setAccountError('');
        // If the shopper edits into a non-Synchrony BIN, drop any CVV they typed while on a
        // Synchrony BIN so we don't accidentally ship a stale security code on the authorize call.
        if (detectFinanceCardType(cleaned) !== 'Synchrony') {
            if (securityCode) setSecurityCode('');
            if (cvvError) setCvvError('');
        }
    };

    const handleSecurityCodeChange = (value: string) => {
        // CVV is 3–4 digits; strip non-digits and cap at 4 (matches the legacy form max-length).
        const cleaned = value.replace(/\D/g, '').slice(0, 4);
        setSecurityCode(cleaned);
        if (cvvError) setCvvError('');
    };

    const handleViewOffers = async () => {
        const cleanAccount = accountNumber.replace(/\D/g, '');
        if (cleanAccount.length < 16) {
            setAccountError(ta('ashleyAdvantage.accountNumberInvalid'));
            return;
        }

        // When the CVV feature is on AND the BIN routes to Synchrony, the security code is
        // required before looking up offers (the value is sent on the Synchrony account-lookup
        // call). Concora (Genesis) BINs skip this — they don't collect a security code (legacy
        // parity — APG's authorization endpoint doesn't accept a securityCode field).
        const cleanCvv = securityCode.replace(/\D/g, '');
        if (cvvEnabled && detectedCardType === 'Synchrony' && cleanCvv.length < 3) {
            setCvvError(ta('ashleyAdvantage.cvvInvalid', { defaultValue: 'Enter the 3 or 4 digit security code.' }));
            return;
        }

        // Guard: never call finance-terms with a non-positive total. The basket total can be
        // momentarily unavailable (pre-calculation / hydration); surface a retry instead of a 400.
        if (!(basketTotal > 0)) {
            setServerError(t('errors:genericTryAgain'));
            setPhase('error');
            return;
        }

        setPhase('loading');
        setServerError('');

        // Cancel any in-flight finance-terms lookup so only the latest attempt can update UI state.
        financeTermsAbortRef.current?.abort();
        const requestId = ++financeTermsRequestIdRef.current;
        const controller = new AbortController();
        financeTermsAbortRef.current = controller;

        try {
            const formData = new FormData();
            formData.set('accountNumber', cleanAccount);
            formData.set('basketTotal', String(basketTotal));
            if (cvvEnabled && detectedCardType === 'Synchrony' && cleanCvv) {
                formData.set('cvv', cleanCvv);
            }

            const res = await fetch('/action/synchrony-finance-terms', {
                method: 'POST',
                body: formData,
                signal: controller.signal,
            });

            const data = (await res.json()) as SynchronyFinanceTermsResponse | SynchronyFinanceTermsErrorResponse;

            // Ignore stale responses from earlier attempts (or aborted requests) that resolved late.
            if (financeTermsRequestIdRef.current !== requestId || controller.signal.aborted) {
                return;
            }

            if (!data.success) {
                // An invalid account number (Synchrony BIN, v3 lookup failed) or an unknown BIN
                // (`timeout:false` — legacy `financeCardTypes` didn't match) is a correctable
                // field-level problem — keep the shopper on the entry form with the message under
                // the input rather than the full "something went wrong" panel. Anything else
                // (SERVICE_UNAVAILABLE, insufficientCredit, activationRequired) surfaces in the
                // prominent error callout.
                if (data.invalidAccount || data.timeout === false) {
                    setAccountError(data.error || ta('ashleyAdvantage.accountNumberInvalid'));
                    setPhase('entry');
                    return;
                }
                setServerError(data.error || t('errors:genericTryAgain'));
                setPhase('error');
                return;
            }

            if (data.terms.length === 0) {
                setServerError(ta('ashleyAdvantage.noTermsAvailable'));
                setPhase('error');
                return;
            }

            setTerms(data.terms);
            setIsGenesisPayment(data.isGenesisPayment === true);
            setProviderDisclosureHtml(data.disclosureHtml ?? '');
            // Legacy parity — the default-selected offer must be one the shopper can
            // actually take. Ineligible terms (below-min-purchase promos like the 48-month
            // FPWI in the reference screenshot) are rendered disabled; falling back to the
            // first term keeps the panel usable when every term happens to be ineligible.
            const defaultTerm = data.terms.find((term) => isTermSelectable(term, basketTotal)) ?? data.terms[0];
            setSelectedTermCode(defaultTerm.code);
            setTermsStep('offerSelection');
            setPhase('terms');
            // Lazily load Synchrony disclosure only. Genesis/Concora terms use APG-provided
            // term disclosure from /finance-terms (legacy parity).
            if (data.isGenesisPayment !== true) {
                fetchDisclosure(defaultTerm);
            }
        } catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError') {
                return;
            }
            // Network / unexpected client-side error — surface a generic message.
            // Server-side action handlers re-throw with structured logging.
            setServerError(t('errors:genericTryAgain'));
            setPhase('error');
        } finally {
            if (financeTermsAbortRef.current === controller) {
                financeTermsAbortRef.current = null;
            }
        }
    };

    // Expose the offers lookup to the parent so the footer "View Financing Options" button can trigger it.
    useImperativeHandle(
        ref,
        () => ({
            viewOffers: () => void handleViewOffers(),
        }),
        // handleViewOffers closes over current state; re-create the handle when inputs change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [accountNumber, securityCode, basketTotal]
    );

    const isSelectedTermSelectable = selectedTerm ? isTermSelectable(selectedTerm, basketTotal) : false;

    const handleSelectTerm = (term: FinancingTerm) => {
        if (!isTermSelectable(term, basketTotal)) return;
        setSelectedTermCode(term.code);
        if (!isGenesisPayment) {
            fetchDisclosure(term);
        }
    };

    const handleAccept = () => {
        if (!selectedTerm || !isSelectedTermSelectable) return;
        const term = selectedTerm;
        const thirdPartyTermCode = resolveThirdPartyTermCode(term);
        onTermSelected({
            // Synchrony v3 authorization requires the ≤4-char ThirdPartyTermCode.
            termCode: thirdPartyTermCode,
            // APG `/authorizations` (Genesis, and Synchrony w/ v3 disabled) requires the APG
            // canonical Code (e.g. "SYNC191", "GEN6003"). Legacy parity with
            // `LibAshleyPayment._addFinanceCardToRequest`, which puts this on `FinancingOptionCode`.
            apgCode: term.code,
            description: term.description,
            accountNumber: accountNumber.replace(/\D/g, ''),
            // Forwarded for the authorization call when enableSynchronyCVV is on AND the account
            // BIN routes to Synchrony. Empty otherwise (feature off, or Concora BIN — legacy
            // parity for SSF-407: Concora doesn't collect a security code).
            securityCode: cvvEnabled && detectedCardType === 'Synchrony' ? securityCode.replace(/\D/g, '') : '',
            // Forward the disclosure HTML the shopper just accepted so the server can stamp it
            // verbatim onto `paymentInstrument.custom.financeTermsAndConditions`. Empty string
            // when Synchrony returned no disclosure — the server falls back to a fresh lookup.
            disclosureText: isGenesisPayment ? sanitizedGenesisDisclosureHtml : sanitizedDisclosureHtml,
        });
    };

    const handleContinueToTerms = () => {
        if (!selectedTermCode || !isSelectedTermSelectable) return;
        setTermsStep('termsAgreement');
    };

    const handleDecline = () => {
        financeTermsAbortRef.current?.abort();
        setPhase('entry');
        setTerms([]);
        setIsGenesisPayment(false);
        setProviderDisclosureHtml('');
        setSelectedTermCode('');
        setTermsStep('offerSelection');
        setServerError('');
    };

    const handleDownloadTerms = () => {
        // Derive plaintext from the rendered DOM so the download always reflects what the shopper
        // actually saw — including any BM edits to `checkout-synchrony-terms-agreement`. `innerText`
        // (not `textContent`) preserves block-level newlines between paragraphs and the heading.
        // Falls back to the heading-only download when the asset is missing/empty.
        const termsSourceText = isGenesisPayment
            ? termsContentRef.current?.innerText
            : termsAgreementBodyRef.current?.innerText;
        const bodyText = termsSourceText?.trim() ?? '';
        const termsAgreementHeading = ta('ashleyAdvantage.termsAgreementHeading', { defaultValue: 'Terms Agreement' });
        const termsText = bodyText ? `${termsAgreementHeading}\n\n${bodyText}` : termsAgreementHeading;

        const blob = new Blob([termsText], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'ashley-advantage-terms.txt';
        document.body.append(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
    };

    const handlePrintTerms = () => {
        const termsHtml = termsContentRef.current?.innerHTML;
        if (!termsHtml) return;

        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        iframe.setAttribute('aria-hidden', 'true');

        const cleanup = () => {
            iframe.remove();
        };

        iframe.onload = () => {
            const printWindow = iframe.contentWindow;
            if (!printWindow) {
                cleanup();
                return;
            }
            printWindow.focus();
            // Delay slightly so the iframe document is fully laid out before print.
            setTimeout(() => {
                printWindow.print();
                cleanup();
            }, 50);
        };

        document.body.append(iframe);
        const iframeDoc = iframe.contentDocument;
        if (!iframeDoc) {
            cleanup();
            return;
        }

        iframeDoc.open();
        iframeDoc.write(
            `<!DOCTYPE html><html><head><title>${ta('ashleyAdvantage.termsDocumentTitle')}</title></head><body>${termsHtml}</body></html>`
        );
        iframeDoc.close();
    };

    useEffect(() => {
        // Keep parent payment section in "terms-only" mode for the entire financing flow
        // (offer selection + terms agreement) so there is only one active CTA at a time.
        onTermsScreenChange?.(phase === 'terms');
    }, [onTermsScreenChange, phase]);

    useEffect(() => {
        return () => {
            financeTermsAbortRef.current?.abort();
        };
    }, []);

    // Subscribe to the streamed Terms Agreement content asset so the parent can gate Accept Terms
    // on whether any legal body will render. The loader already `.catch()`es and resolves the
    // Promise to `null` for any BM failure (missing asset, 403/404, extractor error), so this
    // rejection branch is defensive only.
    useEffect(() => {
        if (!termsAgreementContentAsset) {
            setResolvedTermsAgreementBody(null);
            return;
        }
        let cancelled = false;
        void termsAgreementContentAsset.then(
            (asset) => {
                if (!cancelled) setResolvedTermsAgreementBody(asset?.body ?? null);
            },
            () => {
                if (!cancelled) setResolvedTermsAgreementBody(null);
            }
        );
        return () => {
            cancelled = true;
        };
    }, [termsAgreementContentAsset]);

    return (
        <div className="space-y-4" data-testid="ashley-advantage-payment">
            {phase === 'entry' && (
                <div className="space-y-5 rounded-xs border-input bg-secondary py-6 px-0">
                    <div className="space-y-2">
                        <div className="flex items-center gap-1">
                            <Label
                                htmlFor="aa-account-number"
                                className="gap-0 font-sans text-sm leading-5 font-semibold text-foreground">
                                {ta('ashleyAdvantage.accountNumberLabel')}
                                <span className="text-status-error ml-0.5" aria-hidden>
                                    **
                                </span>
                            </Label>
                            <AshleyAdvantageAccountTooltip />
                        </div>
                        <Input
                            id="aa-account-number"
                            type="text"
                            inputMode="numeric"
                            autoComplete="off"
                            maxLength={19}
                            placeholder={ta('ashleyAdvantage.accountNumberPlaceholder')}
                            value={accountNumber}
                            onChange={(e) => handleAccountNumberChange(e.target.value)}
                            aria-invalid={Boolean(accountError)}
                            aria-describedby={accountError ? 'aa-account-error' : undefined}
                            className="h-14 bg-background font-sans text-sm leading-5"
                        />
                        {accountError && (
                            <p id="aa-account-error" className="text-xs text-status-error" role="alert">
                                {accountError}
                            </p>
                        )}
                    </div>

                    {/* Security code (CVV) — revealed after a full account number when enableSynchronyCVV is on. */}
                    {showCvvField && (
                        <div className="space-y-2" data-testid="aa-security-code-field">
                            <Label
                                htmlFor="aa-security-code"
                                className="gap-0 font-sans text-sm leading-5 font-semibold text-foreground">
                                {ta('ashleyAdvantage.cvvLabel', { defaultValue: 'Security code' })}
                                <span className="text-status-error ml-0.5" aria-hidden>
                                    *
                                </span>
                            </Label>
                            <Input
                                id="aa-security-code"
                                type="text"
                                inputMode="numeric"
                                autoComplete="off"
                                maxLength={4}
                                placeholder={ta('ashleyAdvantage.cvvPlaceholder', { defaultValue: 'CVV' })}
                                value={securityCode}
                                onChange={(e) => handleSecurityCodeChange(e.target.value)}
                                aria-invalid={Boolean(cvvError)}
                                aria-describedby={cvvError ? 'aa-security-code-error' : undefined}
                                className="h-14 w-full bg-background font-sans text-sm leading-5"
                            />
                            {cvvError && (
                                <p id="aa-security-code-error" className="text-xs text-status-error" role="alert">
                                    {cvvError}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Account look-up prompt + button temporarily disabled.
                    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-input bg-transparent pt-4">
                        <p
                            className="font-sans text-sm leading-5 font-normal text-foreground">
                            {ta('ashleyAdvantage.accountLookupPrompt')}
                        </p>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={isLookupLoading}
                            className="h-10 rounded-none px-5 font-sans text-xs leading-4 font-semibold uppercase tracking-button"
                            onClick={handleOpenLookup}>
                            <Search className="size-4" aria-hidden />
                            {ta('ashleyAdvantage.accountLookupButton')}
                        </Button>
                    </div>
                    */}

                    <p className="text-center font-sans text-xs leading-4 font-normal text-foreground">
                        {ta('ashleyAdvantage.noCardPrompt')}{' '}
                        <a
                            href={prequalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-sans text-xs leading-4 font-semibold text-foreground">
                            {ta('ashleyAdvantage.prequalifyLink')}
                        </a>
                    </p>
                </div>
            )}

            {phase === 'error' && (
                <div className="space-y-5" data-testid="ashley-advantage-error">
                    <p className="text-base text-foreground">{ta('ashleyAdvantage.selectFinancingOption')}</p>

                    <div className="flex flex-col items-center gap-6 rounded-lg border border-input bg-card p-10 text-center">
                        <Frown className="size-8 text-muted-foreground" aria-hidden />
                        <div className="space-y-2">
                            <p className="font-serif text-xl text-foreground">{ta('ashleyAdvantage.errorHeading')}</p>
                            <p className="text-sm text-muted-foreground">
                                {serverError || ta('ashleyAdvantage.errorBody')}{' '}
                                {ta('ashleyAdvantage.errorContactBefore')}{' '}
                                <a href="tel:18664363393" className="underline">
                                    {ta('ashleyAdvantage.errorPhone')}
                                </a>{' '}
                                {ta('ashleyAdvantage.errorContactAfter')}
                            </p>
                        </div>
                        <Button type="button" variant="outline" size="sm" onClick={() => void handleViewOffers()}>
                            <RotateCcw className="size-4" aria-hidden />
                            {ta('ashleyAdvantage.retryButton')}
                        </Button>
                    </div>

                    <div className="flex gap-3">
                        <Button type="button" variant="outline" className="flex-1" onClick={handleDecline}>
                            {ta('ashleyAdvantage.backButton')}
                        </Button>
                        <Button type="button" className="flex-1" disabled>
                            {ta('ashleyAdvantage.continueButton')}
                        </Button>
                    </div>
                </div>
            )}

            {phase === 'loading' && (
                <div
                    className="space-y-4"
                    role="status"
                    aria-live="polite"
                    aria-busy="true"
                    data-testid="ashley-advantage-loading">
                    <span className="sr-only">{ta('ashleyAdvantage.loadingOffers')}</span>
                    <Skeleton className="h-4 w-40" />
                    <div className="space-y-2">
                        {[0, 1, 2].map((i) => (
                            <div key={i} className="flex items-start gap-3 border border-input bg-card p-4">
                                <Skeleton className="mt-0.5 size-4 shrink-0 rounded-full" />
                                <div className="flex-1 space-y-2">
                                    <Skeleton className="h-4 w-3/4" />
                                    <Skeleton className="h-3 w-1/3" />
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="flex gap-3">
                        <Skeleton className="h-10 flex-1" />
                        <Skeleton className="h-10 flex-1" />
                    </div>
                </div>
            )}

            <Dialog
                open={phase === 'terms' && terms.length > 0}
                onOpenChange={(open) => {
                    if (!open) handleDecline();
                }}>
                <DialogContent
                    className={TERMS_MODAL_CLASS_BY_STEP[termsStep]}
                    onPointerDownOutside={(event) => event.preventDefault()}
                    onInteractOutside={(event) => event.preventDefault()}
                    data-testid="aa-financing-options-modal">
                    {termsStep === 'offerSelection' ? (
                        <>
                            <DialogHeader className="gap-5 text-left sm:text-left">
                                <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4 ">
                                    <DialogTitle className="font-serif text-2xl font-normal leading-[1.2] text-secondary-foreground">
                                        {ta('ashleyAdvantage.checkoutHeading', {
                                            defaultValue: 'Ashley Advantage Checkout',
                                        })}
                                    </DialogTitle>
                                    <div className="inline-flex items-center gap-1.5 text-secondary-foreground">
                                        <SynchronyIcon aria-hidden />
                                        <span className="text-sm font-normal leading-5 tracking-tight">
                                            {ta('ashleyAdvantage.synchronyLabel', { defaultValue: 'synchrony' })}
                                        </span>
                                    </div>
                                </div>
                                <DialogDescription className="m-0 text-base leading-6 font-normal text-secondary-foreground">
                                    {ta('ashleyAdvantage.selectTermLabel', {
                                        defaultValue: 'Select a financing option:',
                                    })}
                                </DialogDescription>
                            </DialogHeader>
                            <RadioGroup
                                value={selectedTermCode}
                                onValueChange={(value) => {
                                    const nextTerm = terms.find((term) => term.code === value);
                                    if (!nextTerm) return;
                                    handleSelectTerm(nextTerm);
                                }}
                                className="max-h-[52vh] overflow-y-auto gap-0 rounded-xs border border-input">
                                {terms.map((term, index) => {
                                    const minimumPurchaseAmount = resolveMinimumPurchaseAmount(term);
                                    const minimumPurchaseDisplayAmount = minimumPurchaseAmount ?? 0;
                                    const isDisabled = !isTermSelectable(term, basketTotal);
                                    return (
                                        <div
                                            key={term.code}
                                            className={`flex flex-col gap-2 p-5 ${
                                                index > 0 ? 'border-t border-input' : ''
                                            } ${isDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'} ${
                                                selectedTermCode === term.code ? 'bg-secondary' : ''
                                            }`}
                                            aria-disabled={isDisabled || undefined}
                                            onClick={() => handleSelectTerm(term)}
                                            onKeyDown={(event) => {
                                                if (isDisabled) return;
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault();
                                                    handleSelectTerm(term);
                                                }
                                            }}
                                            role="button"
                                            tabIndex={isDisabled ? -1 : 0}>
                                            <div className="flex items-start gap-2">
                                                <RadioGroupItem
                                                    value={term.code}
                                                    disabled={isDisabled}
                                                    className="mt-0.5 size-6 shrink-0"
                                                />
                                                <p
                                                    className={`text-base leading-6 font-semibold ${
                                                        isDisabled ? 'text-muted-foreground' : 'text-foreground'
                                                    }`}>
                                                    {term.description}
                                                </p>
                                            </div>
                                            <div className="space-y-1 pl-6 text-sm leading-5 text-muted-foreground">
                                                <p>
                                                    {ta('ashleyAdvantage.financedLabel', {
                                                        amount: formatUsd(basketTotal),
                                                        defaultValue: 'Financed: {{amount}}',
                                                    })}
                                                </p>
                                                {term.hasDownPayment && term.downPaymentAmount ? (
                                                    <p>
                                                        {ta('ashleyAdvantage.dueNowLabel', {
                                                            amount: formatUsd(term.downPaymentAmount),
                                                            defaultValue: 'Due Now: {{amount}}',
                                                        })}
                                                    </p>
                                                ) : (
                                                    <p>
                                                        {ta('ashleyAdvantage.noMoneyDownLabel', {
                                                            defaultValue: 'No money down',
                                                        })}
                                                    </p>
                                                )}
                                                <p
                                                    className="text-xs italic text-muted-foreground"
                                                    data-testid={`aa-term-minimum-purchase-${term.code}`}>
                                                    {ta('ashleyAdvantage.minimumPurchase', {
                                                        amount: minimumPurchaseDisplayAmount.toFixed(2),
                                                        defaultValue: '${{amount}} Minimum Purchase',
                                                    })}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </RadioGroup>

                            <DialogFooter className="flex-row justify-start gap-2 p-0 sm:justify-start">
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="h-9 flex-1 px-4 text-xs sm:h-8 sm:min-w-[144px] sm:flex-none"
                                    onClick={handleDecline}>
                                    {ta('ashleyAdvantage.backButton', { defaultValue: 'Back' })}
                                </Button>
                                <Button
                                    type="button"
                                    className="h-9 flex-1 px-4 text-xs sm:h-8 sm:min-w-[144px] sm:flex-none"
                                    disabled={!isSelectedTermSelectable}
                                    onClick={handleContinueToTerms}>
                                    {ta('ashleyAdvantage.continueButton', { defaultValue: 'Continue' })}
                                </Button>
                            </DialogFooter>
                        </>
                    ) : (
                        <>
                            <div className="flex items-start justify-between gap-4 border-b border-input pb-4">
                                <div className="space-y-1">
                                    {isGenesisPayment ? (
                                        <h3 className="font-sans text-3xl font-semibold lowercase text-secondary-foreground">
                                            concora
                                            <span className="ml-1 text-lg font-normal text-muted-foreground">
                                                credit
                                            </span>
                                        </h3>
                                    ) : (
                                        <>
                                            <p className="text-xs font-semibold uppercase tracking-button text-muted-foreground">
                                                {ta('ashleyAdvantage.checkoutHeading', {
                                                    defaultValue: 'Ashley Advantage Checkout',
                                                })}
                                            </p>
                                            <h3 className="font-serif text-4xl font-normal leading-none text-secondary-foreground">
                                                {ta('ashleyAdvantage.synchronyFinancialHeading', {
                                                    defaultValue: 'Synchrony Financial',
                                                })}
                                            </h3>
                                        </>
                                    )}
                                </div>
                                <div className="flex items-center gap-4">
                                    <button
                                        type="button"
                                        onClick={handleDownloadTerms}
                                        className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-button text-foreground">
                                        <Download className="size-4" aria-hidden />
                                        {ta('ashleyAdvantage.downloadLabel', { defaultValue: 'Download' })}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handlePrintTerms}
                                        className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-button text-foreground">
                                        <Printer className="size-4" aria-hidden />
                                        {ta('ashleyAdvantage.printLabel', { defaultValue: 'Print' })}
                                    </button>
                                </div>
                            </div>

                            <div
                                className="max-h-[56vh] space-y-4 overflow-y-auto pr-1 text-foreground"
                                ref={termsContentRef}>
                                {isGenesisPayment ? (
                                    hasGenesisDisclosure ? (
                                        <>
                                            {termsAgreementContentAsset ? (
                                                <Suspense fallback={null}>
                                                    <Await resolve={termsAgreementContentAsset}>
                                                        {(asset) => (
                                                            <div
                                                                data-testid="aa-concora-terms-agreement-body"
                                                                // eslint-disable-next-line react/no-danger
                                                                dangerouslySetInnerHTML={{
                                                                    __html: sanitizeProviderHtml(asset?.body ?? ''),
                                                                }}
                                                            />
                                                        )}
                                                    </Await>
                                                </Suspense>
                                            ) : null}
                                            <div
                                                className="text-sm"
                                                data-testid="aa-concora-terms-body"
                                                // eslint-disable-next-line react/no-danger
                                                dangerouslySetInnerHTML={{ __html: sanitizedGenesisDisclosureHtml }}
                                            />
                                        </>
                                    ) : isDisclosureLoading ? (
                                        <div
                                            className="space-y-2 border border-input p-3"
                                            role="status"
                                            aria-live="polite"
                                            aria-busy="true"
                                            data-testid="aa-concora-disclosure-loading">
                                            <span className="sr-only">
                                                {ta('ashleyAdvantage.loadingDisclosure', {
                                                    defaultValue: 'Loading disclosure…',
                                                })}
                                            </span>
                                            <Skeleton className="h-3 w-full" />
                                            <Skeleton className="h-3 w-5/6" />
                                            <Skeleton className="h-3 w-2/3" />
                                        </div>
                                    ) : (
                                        <div
                                            className="flex items-start gap-2 rounded border border-status-error/40 bg-status-error/10 p-3 text-sm text-status-error"
                                            data-testid="aa-concora-disclosure-missing">
                                            <CircleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
                                            <span>
                                                {ta('ashleyAdvantage.errorBody', {
                                                    defaultValue:
                                                        'We could not load the financing disclosure. Please go back and try again.',
                                                })}
                                            </span>
                                        </div>
                                    )
                                ) : (
                                    <div
                                        className="space-y-4 text-foreground"
                                        data-testid="aa-synchrony-terms-body"
                                        ref={termsAgreementBodyRef}
                                    />
                                )}
                                {!isGenesisPayment && (
                                    <>
                                        <p className="font-serif text-2xl font-normal leading-none">
                                            {ta('ashleyAdvantage.termsAgreementHeading', {
                                                defaultValue: 'Terms Agreement',
                                            })}
                                        </p>
                                        {showSynchronyLegalBodyMissing ? (
                                            // Compliance guard: BM Terms Agreement asset resolved
                                            // empty AND Synchrony returned no promo disclosure —
                                            // rendering only a bare "Terms Agreement" heading and
                                            // still allowing the shopper to click Accept Terms
                                            // would let them sign a financing agreement with no
                                            // visible legal body. Mirror the Concora missing-body
                                            // callout and disable Accept Terms below.
                                            <div
                                                className="flex items-start gap-2 rounded border border-status-error/40 bg-status-error/10 p-3 text-sm text-status-error"
                                                data-testid="aa-terms-agreement-missing">
                                                <CircleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
                                                <span>
                                                    {ta('ashleyAdvantage.errorBody', {
                                                        defaultValue:
                                                            'We could not load the financing disclosure. Please go back and try again.',
                                                    })}
                                                </span>
                                            </div>
                                        ) : (
                                            <>
                                                {termsAgreementContentAsset ? (
                                                    <Suspense fallback={null}>
                                                        <Await resolve={termsAgreementContentAsset}>
                                                            {(asset) => (
                                                                <div
                                                                    data-testid="aa-terms-agreement-body"
                                                                    // eslint-disable-next-line react/no-danger
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: sanitizeProviderHtml(asset?.body ?? ''),
                                                                    }}
                                                                />
                                                            )}
                                                        </Await>
                                                    </Suspense>
                                                ) : (
                                                    <div data-testid="aa-terms-agreement-body" />
                                                )}
                                                {isDisclosureLoading ? (
                                                    <div
                                                        className="space-y-2 border border-input p-3"
                                                        role="status"
                                                        aria-live="polite"
                                                        aria-busy="true"
                                                        data-testid="aa-disclosure-loading">
                                                        <span className="sr-only">
                                                            {ta('ashleyAdvantage.loadingDisclosure', {
                                                                defaultValue: 'Loading disclosure…',
                                                            })}
                                                        </span>
                                                        <Skeleton className="h-3 w-full" />
                                                        <Skeleton className="h-3 w-5/6" />
                                                        <Skeleton className="h-3 w-2/3" />
                                                    </div>
                                                ) : (
                                                    disclosureHtml && (
                                                        <div
                                                            className="max-h-40 overflow-y-auto border border-input p-3 text-xs text-muted-foreground"
                                                            data-testid="aa-disclosure"
                                                            // eslint-disable-next-line react/no-danger
                                                            dangerouslySetInnerHTML={{
                                                                __html: sanitizedDisclosureHtml,
                                                            }}
                                                        />
                                                    )
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                            </div>

                            <DialogFooter className="gap-4 p-0 sm:justify-start">
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="h-12 flex-1"
                                    onClick={() => setTermsStep('offerSelection')}>
                                    {ta('ashleyAdvantage.declineTermsButton', { defaultValue: 'Decline' })}
                                </Button>
                                <Button
                                    type="button"
                                    className="h-12 flex-1"
                                    disabled={!isSelectedTermSelectable || showSynchronyLegalBodyMissing}
                                    onClick={handleAccept}>
                                    Accept Terms
                                </Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* AshCom "AA Account Lookup Modal" (Figma node 37303:77569) — server-to-server account
                verification styled to match the design system. The Figma "hosted iframe" placeholder
                is replaced by our in-modal lookup form (verified via /action/synchrony-account-lookup
                → the Custom SCAPI account-lookup endpoint → getAccountInfo). On success the account
                number is prefilled into the entry field and the modal closes. */}
            <Dialog open={lookupOpen} onOpenChange={setLookupOpen}>
                <DialogContent className="gap-6 p-8 sm:max-w-[600px]" data-testid="aa-account-lookup-modal">
                    <img
                        src={ashleyAdvantageLogo}
                        alt={ta('ashleyAdvantage.lookupModalLogoAlt')}
                        className="h-6 w-auto self-start"
                        loading="lazy"
                    />

                    <DialogHeader className="gap-2 text-left sm:text-left">
                        <DialogTitle className="font-serif text-3xl font-normal leading-none text-secondary-foreground">
                            {ta('ashleyAdvantage.lookupModalTitle')}
                        </DialogTitle>
                        <DialogDescription className="text-sm leading-5 font-normal text-secondary-foreground">
                            {ta('ashleyAdvantage.lookupModalDescription')}
                        </DialogDescription>
                    </DialogHeader>

                    {lookupVerified ? (
                        <>
                            <div
                                className="flex items-start gap-3 rounded-lg border border-status-positive/40 bg-status-positive/10 p-6"
                                role="status"
                                data-testid="aa-lookup-verified">
                                <CircleCheck className="mt-0.5 size-6 shrink-0 text-status-positive" aria-hidden />
                                <div className="space-y-1">
                                    <p className="font-sans text-base leading-6 font-semibold text-foreground">
                                        {ta('ashleyAdvantage.lookupVerifiedTitle')}
                                    </p>
                                    <p className="font-sans text-sm leading-5 font-normal text-muted-foreground">
                                        {ta('ashleyAdvantage.lookupVerifiedBody', {
                                            last4: cleanLookupAccount.slice(-4),
                                        })}
                                    </p>
                                </div>
                            </div>

                            <DialogFooter className="gap-4 p-0 sm:justify-start">
                                <Button
                                    type="button"
                                    className="flex-1 px-6"
                                    onClick={handleLookupConfirm}
                                    data-testid="aa-lookup-verified-continue">
                                    {ta('ashleyAdvantage.continueButton')}
                                </Button>
                            </DialogFooter>
                        </>
                    ) : (
                        <>
                            {/* Figma "message" container — the gray card that frames the lookup form. The
                                form fields mirror the legacy Synchrony lookup form (financeterms.isml):
                                required indicator → Ashley Advantage number → optional CVV ("Enter CVV if
                                available") → "Don't have a card? See if you Prequalify". */}
                            <div className="space-y-4 rounded-lg bg-secondary p-6">
                                <p className="font-sans text-xs leading-4 font-semibold uppercase tracking-button text-muted-foreground">
                                    {ta('ashleyAdvantage.requiredLabel')}
                                </p>
                                <div className="space-y-1">
                                    <Label
                                        htmlFor="aa-lookup-account-number"
                                        className="gap-0 font-sans text-sm leading-5 font-semibold text-foreground">
                                        {ta('ashleyAdvantage.accountNumberLabel')}
                                        <span className="text-status-error ml-0.5" aria-hidden>
                                            *
                                        </span>
                                    </Label>
                                    <Input
                                        id="aa-lookup-account-number"
                                        type="text"
                                        inputMode="numeric"
                                        autoComplete="off"
                                        maxLength={19}
                                        placeholder={ta('ashleyAdvantage.accountNumberPlaceholder')}
                                        value={lookupAccountNumber}
                                        onChange={(e) => handleLookupAccountNumberChange(e.target.value)}
                                        aria-invalid={Boolean(lookupAccountError)}
                                        aria-describedby={lookupAccountError ? 'aa-lookup-account-error' : undefined}
                                        className="bg-background font-sans text-sm leading-5"
                                    />
                                    {lookupAccountError && (
                                        <p
                                            id="aa-lookup-account-error"
                                            className="text-xs text-status-error"
                                            role="alert">
                                            {lookupAccountError}
                                        </p>
                                    )}
                                </div>

                                {showLookupCvvField && (
                                    <div className="space-y-1" data-testid="aa-lookup-security-code-field">
                                        <Label
                                            htmlFor="aa-lookup-security-code"
                                            className="gap-0 font-sans text-sm leading-5 font-semibold text-foreground">
                                            {ta('ashleyAdvantage.cvvLabel')}
                                            <span className="text-status-error ml-0.5" aria-hidden>
                                                **
                                            </span>
                                        </Label>
                                        <Input
                                            id="aa-lookup-security-code"
                                            type="text"
                                            inputMode="numeric"
                                            autoComplete="off"
                                            maxLength={4}
                                            placeholder={ta('ashleyAdvantage.cvvPlaceholder')}
                                            value={lookupCvv}
                                            onChange={(e) => handleLookupCvvChange(e.target.value)}
                                            aria-invalid={Boolean(lookupCvvError)}
                                            aria-describedby={
                                                lookupCvvError ? 'aa-lookup-security-code-error' : undefined
                                            }
                                            className="bg-background font-mono w-28"
                                        />
                                        {lookupCvvError ? (
                                            <p
                                                id="aa-lookup-security-code-error"
                                                className="text-xs text-status-error"
                                                role="alert">
                                                {lookupCvvError}
                                            </p>
                                        ) : (
                                            <p className="font-sans text-xs leading-4 font-normal text-muted-foreground">
                                                {ta('ashleyAdvantage.cvvAvailableHint')}
                                            </p>
                                        )}
                                    </div>
                                )}

                                <p className="font-sans text-xs leading-4 font-normal text-foreground">
                                    {ta('ashleyAdvantage.noCardPrompt')}{' '}
                                    <a
                                        href={prequalUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-sans text-xs leading-4 font-semibold uppercase tracking-button underline underline-offset-2">
                                        {ta('ashleyAdvantage.prequalifyLink')}
                                    </a>
                                </p>

                                {lookupError && (
                                    <div
                                        className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive"
                                        role="alert"
                                        data-testid="aa-lookup-error">
                                        <CircleAlert className="mt-0.5 size-5 shrink-0" aria-hidden />
                                        <p className="font-sans text-sm leading-5 font-medium">{lookupError}</p>
                                    </div>
                                )}
                            </div>

                            <DialogFooter className="gap-4 p-0 sm:justify-start">
                                <DialogClose asChild>
                                    <Button type="button" variant="outline" className="px-6">
                                        {ta('ashleyAdvantage.cancelButton')}
                                    </Button>
                                </DialogClose>
                                <Button
                                    type="button"
                                    disabled={isLookupLoading}
                                    className="flex-1 px-6"
                                    onClick={() => void handleLookupSubmit()}>
                                    {isLookupLoading
                                        ? ta('ashleyAdvantage.lookupSubmitLoading')
                                        : ta('ashleyAdvantage.lookupSubmitButton')}
                                </Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
